import React, {useState} from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import NewOrExistingPatient from './NewOrExistingPatient';
import PatientSchedulingLocationForm from './PatientSchedulingLocationForm';
import PatientSchedulingServiceForm from './PatientSchedulingServiceForm';
import PatientSchedulingSearchMethodForm from './PatientSchedulingSearchMethodForm';
import PatientSchedulingProviderForm from './PatientSchedulingProviderForm';
import PatientSchedulingLocationTypeForm from './PatientSchedulingLocationTypeForm';

const steps = ({
  providerId,
  next,
  setLocation,
  setService,
  setSearchMethod,
  setProviderId,
  setProviderAvailabilityId,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveLocationType,
  navigate,
}) => [
  {
    stepIndex: 0,
    stepNum: -1,
    padding: '2em',
    Component: NewOrExistingPatient,
    onSubmit: (e, isNewPatient) => {
      if (isNewPatient) {
        next();
      } else {
        navigate('/login');
      }
    },
  },
  {
    stepIndex: 1,
    stepNum: 0,
    padding: '2em',
    Component: PatientSchedulingSearchMethodForm,
    onSubmit: (e, searchMethod) => {
      setSearchMethod(searchMethod);
      next();
    },
  },
  {
    stepIndex: 2,
    stepNum: 1,
    padding: '2em',
    Component: PatientSchedulingLocationForm,
    onSubmit: (e, address) => {
      if (address) {
        setLocation(address);
      } else {
        setLocation(e?.target?.value);
      }
      next();
    },
  },
  {
    stepIndex: 3,
    stepNum: 2,
    padding: '2em',
    Component: PatientSchedulingServiceForm,
    onSubmit: (e, serviceCat) => {
      setService(serviceCat);
      next();
    },
  },
  {
    stepIndex: 4,
    stepNum: 3,
    padding: '0em',
    Component: PatientSchedulingProviderForm,
    onSubmit: (e, {isoString, providerId, providerAvailabilityId, sessionTypeId}) => {
      // The form stores its state in redux automatically
      const dateStr = moment(isoString).format('MM/DD/yyyy');
      const timeStr = moment(isoString).format('hh:mm a');
      saveDate(dateStr);
      saveTime(timeStr);
      saveSessionTypeId(sessionTypeId);
      saveProviderAvailabilityId(providerAvailabilityId);
      setProviderId(providerId);
      setProviderAvailabilityId(providerAvailabilityId);
      next();
    },
  },
  {
    stepIndex: 5,
    stepNum: 4,
    padding: '2em',
    Component: PatientSchedulingLocationTypeForm,
    onSubmit: (e, locationType) => {
      saveLocationType(locationType);
      navigate(`/signup?alreadyScheduled=true&selectedProviderId=${providerId}`);
      next();
    },
  },
];

const PublicPatientScheduling = props => {
  const [stepIndex, setStepIndex] = useState(0);
  const [location, setLocation] = useState(null);
  const [serviceCategory, setService] = useState(null);
  const [searchMethod, setSearchMethod] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [providerAvailabilityId, setProviderAvailabilityId] = useState(null);
  const navigate = useNavigate();
  const next = () => setStepIndex(stepIndex + 1);
  const stepConfig = steps({
    providerId,
    next,
    setLocation,
    setService,
    setSearchMethod,
    setProviderId,
    setProviderAvailabilityId,
    saveDate: props.saveDate,
    saveTime: props.saveTime,
    saveSessionTypeId: props.saveSessionTypeId,
    saveProviderAvailabilityId: props.saveProviderAvailabilityId,
    saveLocationType: props.saveLocationType,
    navigate,
  }).find(step => step.stepIndex === stepIndex);
  const childProps = {
    stepIndex,
    location, 
    serviceCategory,
    searchMethod,
    providerAvailabilityId,
    onSubmit: stepConfig.onSubmit,
  };
  return (
    <Container maxWidth="md">
      <Stack spacing={2} alignItems='center' mt={4}>
        <Stack alignItems='center'>
          <Box style={{maxWidth: '350px'}}>
            <img src='/images/MovementX-logo.png' alt='MovementX Logo' />
          </Box>
        </Stack>
        <Typography variant='h1'>
          Find Your Perfect Provider
        </Typography>
        <Typography variant='body1'>
          Book with top-rated providers in your area.
        </Typography>
        <Box 
          style={{
            marginTop: '2em',
            padding: stepConfig.padding,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            border: '1px solid rgb(240,240,240)',
            width: '100%',
          }}
        >
          {stepIndex > 0 &&
            <Button 
              onClick={() => setStepIndex(stepIndex - 1)} 
              size='small'
              startIcon={<ChevronLeftIcon/>}
              style={
                stepConfig?.padding === '0em'
                ? {marginLeft: '2em', marginTop: '2em'}
                : {} 
              }
            >
              Back
            </Button>
          }
          <stepConfig.Component {...childProps} />
        </Box>
      </Stack>
    </Container>
  );
};

export default PublicPatientScheduling;